import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import MainMenu from '@/components/MainMenu';
import SeoHead from '@/components/SeoHead';
import SiteDateReadout from '@/components/SiteDateReadout';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SeoHead
        title="Privacy Policy | JobFit AI"
        description="Read how JobFit AI handles account data, resume and job description content, payments, email communications, and service providers."
        canonicalPath="/privacy"
        robots="index,follow"
      />
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="mb-6 flex items-center gap-3">
          <MainMenu />
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>

        <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-border bg-muted/30">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="h-6 w-6 text-primary" />
              <h1 className="font-heading text-2xl font-bold">Privacy Policy</h1>
            </div>
            <p className="text-sm text-muted-foreground">Effective Date: April 11, 2026</p>
            <p className="text-sm text-muted-foreground">Last Updated: April 26, 2026</p>
          </div>

          <div className="px-6 py-8 space-y-8 text-sm leading-7">
            <section>
              <p>
                JobFit AI and SparkFlow Automation (“JobFit AI,” “SparkFlow Automation,” “we,” “us,”
                or “our”) respect your privacy. This Privacy Policy explains how we collect, use,
                disclose, store, and protect your information when you access or use our website,
                applications, mobile applications, and related services (collectively, the “Service”).
              </p>
              <p className="mt-4">
                By accessing or using the Service, you acknowledge that we may collect, use, and
                disclose information as described in this Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold mb-3">1. Scope of This Privacy Policy</h2>
              <p>
                This Privacy Policy applies to information we collect through the Service, including
                when you create an account, submit resume or job-related content, use our AI-powered
                features, purchase a subscription, contact us, or otherwise interact with the Service.
              </p>
              <p className="mt-4">
                The Service is intended for adults age 18 and older and is designed primarily for users
                seeking resume-related assistance aligned with United States hiring and resume conventions,
                although the Service may be accessed from other countries.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold mb-3">2. Information We Collect</h2>
              <p>We may collect the following categories of information:</p>

              <h3 className="font-semibold mt-4 mb-2">A. Information You Provide Directly</h3>
              <p>When you use the Service, you may provide us with information such as:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>email address</li>
                <li>password</li>
                <li>resume content</li>
                <li>employment history</li>
                <li>education history</li>
                <li>skills, certifications, and qualifications</li>
                <li>job description text and related materials</li>
                <li>prompts, instructions, and other content submitted to generate or analyze resumes</li>
                <li>support requests, emails, and feedback you send to us</li>
              </ul>
              <p className="mt-4">
                Please do not submit sensitive personal information unless it is necessary for your
                intended use of the Service.
              </p>

              <h3 className="font-semibold mt-4 mb-2">B. Account and Subscription Information</h3>
              <p>We may collect or maintain account-related information and metadata, such as:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>account identifiers</li>
                <li>email verification status</li>
                <li>password reset and account-security records</li>
                <li>subscription or plan status</li>
                <li>internal account settings and administrative flags</li>
              </ul>

              <h3 className="font-semibold mt-4 mb-2">C. Payment and Billing Information</h3>
              <p>
                If you purchase a paid subscription, payment processing is handled by Stripe. We do not
                store your full payment card number or card security code. We may receive limited billing
                and subscription information from Stripe, such as:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Stripe customer ID</li>
                <li>subscription status</li>
                <li>payment status</li>
                <li>plan type</li>
                <li>billing period details</li>
                <li>limited transaction or invoice-related metadata</li>
              </ul>

              <h3 className="font-semibold mt-4 mb-2">D. Automatically Collected Information</h3>
              <p>
                When you use the Service, we may automatically collect certain technical and usage
                information, such as:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>IP address</li>
                <li>browser type</li>
                <li>device type</li>
                <li>operating system</li>
                <li>log data</li>
                <li>access times</li>
                <li>pages or features used</li>
                <li>general usage and interaction data</li>
              </ul>

              <h3 className="font-semibold mt-4 mb-2">E. Cookies and Similar Technologies</h3>
              <p>
                We may use cookies and similar technologies to support authentication, session management,
                security, basic site functionality, and performance. If analytics or tracking tools are
                added in the future, this Privacy Policy may be updated accordingly.
              </p>
              <p className="mt-4">
                You can control cookies through your browser settings, but disabling cookies may affect
                the functionality of parts of the Service.
              </p>

              <h3 className="font-semibold mt-4 mb-2">F. Saved Resumes, Saved Sessions, and Generated Content</h3>
              <p>
                Uploaded resumes may be saved to your account for later use. Generated resumes may also
                be saved to your account for later use. When a resume is saved, we generally store the
                resume as extracted text, structured text data, generated resume text, related metadata,
                or similar text-based information in our database. We do not save saved resumes as PDF
                or DOCX files for account storage, and we do not necessarily preserve or store the
                original uploaded file, original file format, layout, formatting, filename, or uploaded
                binary file.
              </p>
              <p className="mt-4">
                Saved resumes may be available in dropdowns or selection tools across different features.
                When you select a saved resume, the saved resume text may populate or replace the current
                resume text field for that feature. The AI feature then processes the selected resume
                text as part of the requested feature.
              </p>
              <p className="mt-4">
                We may use saved resume text, structured text data, or generated content to create or
                regenerate downloadable files, such as DOCX or PDF exports, when an export feature is
                available. The exported file is generated from stored text-based data and is not
                necessarily a copy of any original uploaded file.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold mb-3">3. How We Use Information</h2>
              <p>We may use personal information for the following purposes:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>to create and manage user accounts</li>
                <li>to authenticate users and maintain account security</li>
                <li>to provide resume evaluation, resume-to-job-description comparison, and custom resume generation features</li>
                <li>to process and respond to submitted resume and job-related content</li>
                <li>to generate downloadable outputs such as PDF or DOCX files</li>
                <li>to process subscriptions and manage billing</li>
                <li>to send transactional emails, including verification emails, password reset emails, billing notices, and service notifications</li>
                <li>to respond to support requests and user inquiries</li>
                <li>to troubleshoot issues, monitor performance, maintain the Service, and improve functionality</li>
                <li>to detect, investigate, prevent, or address fraud, abuse, security incidents, and violations of our agreements</li>
                <li>to comply with legal obligations and protect our rights, users, and systems</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold mb-3">4. AI Processing and Third-Party Model Providers</h2>
              <p>
                To provide AI-powered features, we may send content you submit through the Service to
                third-party AI providers, including OpenAI and Anthropic, as necessary to provide the
                requested feature.
              </p>
              <p className="mt-4">This may include:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>resume text</li>
                <li>job description text</li>
                <li>prompts and instructions you submit</li>
                <li>related generated or transformed content</li>
              </ul>
              <p className="mt-4">
                We use these providers to perform tasks such as resume evaluation, resume comparison,
                and customized resume generation.
              </p>
              <p className="mt-4">
                We use third-party AI providers to process user-submitted content to provide the Service.
                We do not intentionally allow providers to use customer content for training unless their
                applicable service settings, contracts, or policies permit such use and such use has been
                enabled by us.
              </p>
              <p className="mt-4">
                However, third-party providers operate under their own terms, policies, infrastructure,
                and retention practices. We encourage users not to submit confidential or highly sensitive
                information unless necessary for the Service.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold mb-3">5. Sources of Information</h2>
              <p>We collect personal information from the following sources:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>directly from you when you create an account, enter information, or contact us</li>
                <li>automatically from your device or browser when you use the Service</li>
                <li>from payment processors and service providers that support subscriptions, authentication, communications, hosting, and infrastructure</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold mb-3">6. How We Share Information</h2>
              <p>
                We may share personal information with trusted service providers and vendors that help us
                operate the Service, including:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Stripe for payment processing and subscription management</li>
                <li>Resend for transactional email delivery</li>
                <li>OpenAI and Anthropic for AI-powered processing</li>
                <li>hosting, infrastructure, database, and security providers, including providers used to host our frontend, backend, and database systems</li>
              </ul>
              <p className="mt-4">We may also disclose information:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>if required by law, regulation, subpoena, court order, or legal process</li>
                <li>to enforce our agreements or protect our rights, users, systems, or property</li>
                <li>to detect or prevent fraud, abuse, or security incidents</li>
                <li>in connection with an actual or proposed merger, acquisition, financing, restructuring, or sale of assets</li>
                <li>with your direction, consent, or instruction</li>
              </ul>
              <p className="mt-4">We do not sell your personal information for monetary consideration.</p>
              <p className="mt-4">
                We do not currently knowingly share personal information for cross-context behavioral
                advertising or retargeting. If that changes, we will update this Privacy Policy before
                such practices begin.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold mb-3">7. Data Retention</h2>
              <p>
                We retain personal information for as long as reasonably necessary for the purposes
                described in this Privacy Policy, including to:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>provide and maintain the Service</li>
                <li>keep your account active</li>
                <li>process transactions and maintain billing records</li>
                <li>meet legal, tax, accounting, and compliance obligations</li>
                <li>resolve disputes</li>
                <li>enforce agreements</li>
                <li>protect the security and integrity of the Service</li>
              </ul>
              <p className="mt-4">
                Retention periods may vary depending on the type of information and the reason it was
                collected. Deleted or inactive data may persist for a limited period in backups, logs,
                archives, or security systems. Payment- and subscription-related records may be retained
                as required for legal, accounting, fraud-prevention, and operational purposes.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold mb-3">8. Account Deletion and Privacy Requests</h2>
              <p>
                Users may request account deletion or submit privacy-related requests through the methods
                made available in the Service or by contacting us at:
              </p>
              <p className="mt-4 font-medium">sparkflowautomation@gmail.com</p>
              <p className="mt-4">
                We may take reasonable steps to verify your identity before processing a request, including
                verifying control of the email address associated with the account.
              </p>
              <p className="mt-4">
                When you request deletion, we will delete or de-identify information as required or
                appropriate, subject to exceptions for legal compliance, billing records, fraud prevention,
                dispute resolution, security, backup systems, and enforcement of our agreements.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold mb-3">9. Your Privacy Rights</h2>
              <p>
                Depending on where you live, you may have rights regarding your personal information,
                including the right to:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>request access to certain personal information we hold about you</li>
                <li>request correction of inaccurate personal information</li>
                <li>request deletion of certain personal information</li>
                <li>request information about categories of personal information collected, used, disclosed, or shared</li>
                <li>appeal the denial of a privacy request where required by applicable law</li>
                <li>opt out of certain data-sharing practices if such practices become applicable in the future</li>
              </ul>
              <p className="mt-4">To submit a privacy request, contact us at:</p>
              <p className="mt-4 font-medium">sparkflowautomation@gmail.com</p>
              <p className="mt-4">
                We will respond in accordance with applicable law and may need to verify your identity
                before fulfilling a request.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold mb-3">10. International Users</h2>
              <p>
                JobFit AI is operated using service providers and infrastructure that may be located in the
                United States and other countries. If you access the Service from outside the United States,
                your information may be transferred to, stored in, and processed in countries whose data
                protection laws may differ from those in your jurisdiction.
              </p>
              <p className="mt-4">
                By using the Service, you understand that your information may be transferred to and
                processed in the United States and other countries where our service providers operate.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold mb-3">11. Data Security</h2>
              <p>
                We use reasonable administrative, technical, and organizational safeguards designed to
                protect personal information. However, no method of transmission over the Internet or
                method of storage is completely secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold mb-3">12. Children’s Privacy</h2>
              <p>
                The Service is intended only for adults age 18 and older. The Service is not directed to
                children under 13, and we do not knowingly collect personal information from children
                under 13.
              </p>
              <p className="mt-4">
                If you believe that a child under 13 has provided personal information to us, please
                contact us at sparkflowautomation@gmail.com, and we will take appropriate steps to review
                and address the issue.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold mb-3">13. Third-Party Services and Links</h2>
              <p>
                The Service may contain links to third-party websites or rely on third-party services that
                we do not control. We are not responsible for the privacy, security, or data practices of
                third-party websites or services. We encourage you to review the privacy policies of those
                third parties before providing information to them.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold mb-3">14. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. If we make material changes, we will
                revise the “Last Updated” date and may provide additional notice where appropriate. Your
                continued use of the Service after the updated Privacy Policy becomes effective means you
                acknowledge the revised Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold mb-3">15. Contact Us</h2>
              <p>If you have questions, concerns, or privacy-related requests, you may contact:</p>
              <div className="mt-4">
                <p>JobFit AI / SparkFlow Automation</p>
                <p>Email: sparkflowautomation@gmail.com</p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <footer className="py-8 px-6 border-t border-border mt-10">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
          <div className="flex flex-col items-center sm:items-start gap-2">
            <p>© 2026 JobFit AI. All rights reserved.</p>
            <SiteDateReadout />
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
